import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { analytics } from '../../services/analytics';
import SEOPageLayout from '../../components/SEOPageLayout';

const CheckIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const XIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ExcelVsSoftware = () => {
  useEffect(() => {
    analytics.track('Comparison Page View', { comparison: 'Excel vs Software' });
  }, []);

  return (
    <SEOPageLayout
      title="Excel Quote Templates vs Quoting Software | QuoteTier"
      description="Still using Excel or Word to make quotes? See how dedicated quoting software saves time, reduces errors, and wins more jobs."
      canonical="/compare/excel-vs-quoting-software"
      ogTitle="Stop Quoting in Spreadsheets"
      ogDescription="Excel is for accountants. QuoteTier is for closers. See why moving to dedicated software pays for itself in one job."
    >
      <section className="py-32 md:py-40 lg:py-48">
        <div className="container max-w-7xl mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="font-instrument text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-tight mb-6">
              Excel vs. <span className="text-primary font-bold">Quoting Software</span>
            </h1>
            <p className="font-jetbrains text-xl md:text-2xl text-gray-600 tracking-tight leading-relaxed mb-8">
              Spreadsheets are free, but they are costing you a fortune in lost time and missed opportunities.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-6 px-6 font-instrument text-lg font-semibold text-gray-900 bg-gray-50">Feature</th>
                  <th className="text-center py-6 px-6 bg-black text-white">
                      <span className="font-instrument text-lg font-bold">QuoteTier</span>
                  </th>
                  <th className="text-center py-6 px-6 font-instrument text-lg font-semibold text-gray-700 bg-gray-50">Excel / Word</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-100">
                  <td className="py-4 px-6 font-jetbrains font-medium">Mobile Friendly</td>
                  <td className="py-4 px-6 text-center text-green-600 font-bold">100% Optimized</td>
                  <td className="py-4 px-6 text-center text-red-500">Unreadable on phone</td>
                </tr>
                <tr className="border-b border-gray-100 bg-gray-50/30">
                  <td className="py-4 px-6 font-jetbrains font-medium">Tracking</td>
                  <td className="py-4 px-6 text-center text-green-600 font-bold">See when they open it</td>
                  <td className="py-4 px-6 text-center text-red-500">Black hole</td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-4 px-6 font-jetbrains font-medium">E-Signature</td>
                  <td className="py-4 px-6 text-center text-green-600 font-bold">Built-in</td>
                  <td className="py-4 px-6 text-center">Requires print/scan or extra tool</td>
                </tr>
                <tr className="border-b border-gray-100 bg-gray-50/30">
                  <td className="py-4 px-6 font-jetbrains font-medium">Time to Create</td>
                  <td className="py-4 px-6 text-center text-green-600 font-bold">~2 Minutes</td>
                  <td className="py-4 px-6 text-center">15-30 Minutes</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container max-w-4xl mx-auto px-4">
            <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-6">The Hidden Cost of "Free"</h2>
            <p className="font-jetbrains text-lg text-gray-700 mb-6">
                Excel is great for crunching numbers, but it's terrible at selling.
            </p>
            <ul className="list-disc pl-6 space-y-4 font-jetbrains text-lg text-gray-700 mb-6">
                <li><strong>Copy/Paste Errors:</strong> How many times have you forgotten to update a formula or change the client name?</li>
                <li><strong>Version Control Hell:</strong> "Quote_Final_v3_REAL.pdf"</li>
                <li><strong>No Analytics:</strong> You send the PDF and wait. Did they see it? Did it go to spam? You have no idea.</li>
                <li><strong>Friction:</strong> To sign your Excel quote, they have to print it, sign it, scan it, and email it back. That's a deal killer.</li>
            </ul>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-black text-white">
        <div className="container max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-6">Upgrade Your Workflow</h2>
          <Link to="/signup">
            <Button className="px-10 py-4 font-instrument text-xl font-semibold bg-white text-black hover:bg-gray-200 rounded-lg shadow-lg">
              Start Free
            </Button>
          </Link>
        </div>
      </section>
    </SEOPageLayout>
  );
};

export default ExcelVsSoftware;
